import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, EditableText, NonIdealState } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { TorgaContext } from '../TorgaContext';
import { Page } from '../../utils/Layout/Layout';

import { actionGoalUpdate, actionGoalDelete } from './actions';
import TaskList from '../Task/TaskList';
import { urlToActivityID } from '../TorgaRouter';
import { Link } from 'react-router-dom';

const panelStyle = {
  border: '1px solid #2f3a45',
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
};

const panelTitleStyle = { marginTop: 0, marginBottom: 10 };

const fieldBlockStyle = { marginBottom: 10 };

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
};

const inputStyle = { width: '100%', padding: 8 };

const readOnlyGridStyle = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr',
  gap: 6,
  alignItems: 'center',
};

export default function Goal() {
  const { state, dispatch, dispatchAction } = React.useContext(TorgaContext);
  const [isNameEditing, setIsNameEditing] = React.useState(false);
  const [activitySearch, setActivitySearch] = React.useState('');
  const [selectedActivityID, setSelectedActivityID] = React.useState('');
  const [draft, setDraft] = React.useState({
    uuid: '',
    description: '',
    status: '',
    notes: '',
  });
  const location = useLocation();
  const navigate = useNavigate();
  let { id: prefixedID } = useParams();
  const id = prefixedID?.slice(1);

  const { [id]: goal } = state.goals.byID;
  const allActivities = state.activities.allIDs
    .map((activityId) => state.activities.byID[activityId])
    .filter((activity) => activity && !activity.deleted);
  const filteredActivities = allActivities.filter((activity) => {
    if (!activitySearch.trim()) {
      return true;
    }
    const searchTerm = activitySearch.toLowerCase();
    return String(activity.name || '').toLowerCase().includes(searchTerm);
  });
  const currentActivity =
    (goal.activity_id && state.activities.byID[goal.activity_id]) || null;

  React.useEffect(() => {
    if (!goal) {
      return;
    }
    if (!location.state?.focusName) {
      return;
    }
    setIsNameEditing(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [goal, location.pathname, location.state, navigate]);

  React.useEffect(() => {
    if (!goal) {
      return;
    }

    setDraft({
      uuid: goal.uuid || '',
      description: goal.description || '',
      status: goal.status || '',
      notes: goal.notes || '',
    });
  }, [goal]);

  if (!goal) {
    return (
      <NonIdealState
        icon={IconNames.TARGET}
        description="Goals are what makes you activities live, they come suggested by AI, or manually created to your needs. Goals are what you want to achieve, complete is with tasks"
      />
    );
  }

  function handleChange(field, value) {
    dispatch({ type: 'GOAL_CHANGE', payload: { id, field, value } });
  }

  function handleConfirm(field, value) {
    dispatchAction(actionGoalUpdate(id, field, value));
  }

  function normalizeNullableString(value) {
    const trimmed = String(value || '').trim();
    return trimmed ? trimmed : null;
  }

  return (
    <Page>
      <h1>
        <EditableText
          data-testid="goal-name-editable"
          placeholder="Goal Name"
          value={goal.name}
          onChange={(e) => handleChange('name', e)}
          onConfirm={(e) => handleConfirm('name', e)}
          onEdit={(editing) => setIsNameEditing(editing)}
          isEditing={isNameEditing}
          selectAllOnFocus={true}
        />
      </h1>

      <div style={panelStyle}>
        <h3 style={panelTitleStyle}>Editable fields</h3>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Activity (optional)</label>
          <input
            type="text"
            style={{ ...inputStyle, marginBottom: 8 }}
            placeholder="Search activity..."
            value={activitySearch}
            onChange={(event) => setActivitySearch(event.target.value)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              style={{ ...inputStyle, flex: 1 }}
              value={selectedActivityID}
              onChange={(event) => setSelectedActivityID(event.target.value)}
            >
              <option value="">Select activity...</option>
              {filteredActivities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  A{activity.id} - {activity.name}
                </option>
              ))}
            </select>
            <Button
              small
              disabled={!selectedActivityID}
              onClick={() =>
                handleConfirm('activity_id', Number(selectedActivityID))
              }
            >
              Associate
            </Button>
            <Button
              small
              disabled={!goal.activity_id}
              onClick={() => handleConfirm('activity_id', null)}
            >
              Dissociate
            </Button>
          </div>
          {currentActivity && (
            <div style={{ marginTop: 6 }}>
              Current: <Link to={urlToActivityID(currentActivity.id)}>A{currentActivity.id} - {currentActivity.name}</Link>
            </div>
          )}
        </div>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>UUID</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              style={inputStyle}
              value={draft.uuid}
              onChange={(event) =>
                setDraft((current) => ({ ...current, uuid: event.target.value }))
              }
            />
            <Button
              small
              onClick={() => handleConfirm('uuid', normalizeNullableString(draft.uuid))}
            >
              Save
            </Button>
          </div>
        </div>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Description</label>
          <textarea
            style={inputStyle}
            value={draft.description}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
            rows={4}
          />
          <div style={{ marginTop: 6 }}>
            <Button
              small
              onClick={() =>
                handleConfirm(
                  'description',
                  normalizeNullableString(draft.description)
                )
              }
            >
              Save description
            </Button>
          </div>
        </div>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Status</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              style={inputStyle}
              value={draft.status}
              onChange={(event) =>
                setDraft((current) => ({ ...current, status: event.target.value }))
              }
            />
            <Button
              small
              onClick={() =>
                handleConfirm('status', normalizeNullableString(draft.status))
              }
            >
              Save
            </Button>
          </div>
        </div>

        <div style={fieldBlockStyle}>
          <label style={labelStyle}>Notes</label>
          <textarea
            style={inputStyle}
            value={draft.notes}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                notes: event.target.value,
              }))
            }
            rows={4}
          />
          <div style={{ marginTop: 6 }}>
            <Button
              small
              onClick={() =>
                handleConfirm('notes', normalizeNullableString(draft.notes))
              }
            >
              Save notes
            </Button>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={goal.deleted === true}
            onChange={(event) => handleConfirm('deleted', event.target.checked)}
          />
          deleted
        </label>
      </div>

      <div style={panelStyle}>
        <h3 style={panelTitleStyle}>DB fields (read-only)</h3>
        <div style={readOnlyGridStyle}>
          <strong>id</strong>
          <code>{goal.id}</code>
          <strong>user_id</strong>
          <code>{goal.user_id}</code>
          <strong>createdAt</strong>
          <code>{goal.createdAt ? String(goal.createdAt) : '-'}</code>
          <strong>updatedAt</strong>
          <code>{goal.updatedAt ? String(goal.updatedAt) : '-'}</code>
          <strong>activity_id</strong>
          <code>{goal.activity_id ?? '-'}</code>
        </div>
      </div>

      <p>This goal is tied to activity:</p>
      {!currentActivity ? (
        <NonIdealState
          icon={IconNames.PULSE}
          description="This goal is currently not linked to an activity."
        />
      ) : (
        <div>
          <Link to={urlToActivityID(currentActivity.id)}>{currentActivity.name}</Link>
        </div>
      )}

      <Button
        data-testid="goal-delete-button"
        icon={IconNames.TRASH}
        minimal
        onClick={() => dispatchAction(actionGoalDelete(id))}
      >
        Delete goal
      </Button>

      <TaskList goalID={id} activityID={goal.activity_id} />
    </Page>
  );
}
